/* global React, Icon, Button, Field, Input, Select, Currency, Segmented, Modal, InvoiceCommentField, toast, fmt, t, invoiceListCustomerEmail, invoiceListSendPayload, invoiceListSendSubject, invoiceListOutstandingAmount, invoiceListAddDays, invoiceListTodayIso, invoiceListIsoToNb, invoiceListTypeLabel, invoiceListCreditMinimumDate */
// PLUMME 3 - Massehandlinger i fakturalisten.
//
// Lista er stedet man har mange dokumenter foran seg samtidig, og da er det
// raden man vil gjore noe med — ikke ett dokument av gangen inne i detaljen.
// Denne fila eier hvem en massehandling kan treffe, dialogen som bekrefter den,
// og kjoringen. `invoice-list.jsx` eier utvalget og lista.
//
// Hver handling har én kilde til hvem den treffer (`invoiceListBulkTargets`), slik
// at knappen i baren, teksten i dialogen og selve kjoringen ikke kan bli uenige om
// hvilke rader som faktisk blir behandlet.

// Importert historikk er lesestoff, ikke dokumenter man kan handle paa. Alt annet
// kan velges — ogsaa utkast, som har egne handlinger lenger ned.
function invoiceListCanBulkSelect(inv) {
  return Boolean(inv && !inv.importedReadOnly);
}

function invoiceListIsDraft(inv) {
  return Boolean(inv && inv.status === 'utkast');
}

// Opprettede dokumenter. Et utkast er ikke sendt for, har ikke nummer, og kan
// derfor ikke sendes «paa nytt» eller krediteres.
function invoiceListCanBulkResend(inv) {
  return invoiceListCanBulkSelect(inv) && !invoiceListIsDraft(inv);
}

function invoiceListCanBulkMarkDelivered(inv) {
  return invoiceListCanBulkResend(inv)
    && !inv.deliveredExternallyAt
    && (!inv.sentAt || inv.delivery?.failed);
}

function invoiceListCanBulkCredit(inv) {
  return invoiceListCanBulkResend(inv) && inv.type === 'faktura' && !inv.fullyCredited;
}

function invoiceListCanBulkRemind(inv) {
  return invoiceListCanBulkResend(inv) && inv.type === 'faktura' && inv.paymentStatus !== 'paid' && inv.status !== 'betalt';
}

// Et utkast kan opprettes naar det har kunde og minst én linje — samme krav som
// «Opprett» i salgseditoren stiller.
function invoiceListCanBulkIssue(inv) {
  if (!invoiceListCanBulkSelect(inv) || !invoiceListIsDraft(inv)) return false;
  const backend = inv._backend || inv;
  const customer = String(backend.customer?.name || '').trim();
  const hasCustomer = Boolean(customer) && customer !== 'Uten kunde';
  const hasLine = (Array.isArray(backend.lines) ? backend.lines : [])
    .some((line) => String(line?.description || line?.productName || '').trim());
  return hasCustomer && hasLine;
}

function invoiceListCanBulkDeleteDraft(inv) {
  return invoiceListCanBulkSelect(inv) && invoiceListIsDraft(inv);
}

// En kreditnota som skal bokfoeres maa ha en aarsak. Utkast laget ved kreditering
// fra fakturalista har den ikke, fordi brukeren aldri saa et skjema.
function invoiceListDraftNeedsCreditReason(inv) {
  if (!invoiceListIsDraft(inv) || inv.type !== 'kreditnota') return false;
  return !String((inv._backend || inv).creditReason || '').trim();
}

const INVOICE_BULK_TAB_ACTIONS = {
  faktura: ['deliver', 'send', 'reminder', 'credit'],
  tilbud: ['deliver', 'send'],
  ordre: ['deliver', 'send'],
  kreditnota: ['deliver', 'send'],
  utkast: ['issueOnly', 'issue', 'delete'],
};

// Handlingene baren kan tilby. `eligible` er den eneste definisjonen av hvem
// handlingen treffer.
function invoiceListBulkActionDef(key) {
  if (key === 'deliver') return { key, icon: 'check', variant: 'secondary', label: t('inv.bulk.deliver'), eligible: invoiceListCanBulkMarkDelivered };
  if (key === 'send') return { key, icon: 'send', variant: 'secondary', label: t('inv.bulk.resend'), eligible: (inv) => invoiceListCanBulkResend(inv) && Boolean(invoiceListCustomerEmail(inv)) };
  if (key === 'reminder') return { key, icon: 'bell', variant: 'secondary', label: t('inv.bulk.reminder'), eligible: (inv) => invoiceListCanBulkRemind(inv) && Boolean(invoiceListCustomerEmail(inv)) };
  if (key === 'credit') return { key, icon: 'files', variant: 'danger', label: t('inv.bulk.credit'), eligible: invoiceListCanBulkCredit };
  if (key === 'issue') return { key, icon: 'send', variant: 'primary', label: t('inv.bulk.issue'), eligible: invoiceListCanBulkIssue };
  if (key === 'issueOnly') return { key, icon: 'fileText', variant: 'secondary', label: t('inv.bulk.mode.create'), eligible: invoiceListCanBulkIssue };
  if (key === 'delete') return { key, icon: 'trash', variant: 'danger', label: t('inv.bulk.delete'), eligible: invoiceListCanBulkDeleteDraft };
  return null;
}

function invoiceListBulkTargets(action, rows) {
  const def = invoiceListBulkActionDef(action);
  if (!def) return [];
  return (rows || []).filter(def.eligible);
}

function invoiceListBulkActionsForTab(tab) {
  return (INVOICE_BULK_TAB_ACTIONS[tab] || ['send']).map(invoiceListBulkActionDef).filter(Boolean);
}

function InvoiceBulkBar({ tab, rows, selectedRows, allSelected, someSelected, onSelectAll, onClear, onAction }) {
  const actions = invoiceListBulkActionsForTab(tab);
  return <div className="fak-bulkbar">
    <button type="button" className={'inbox-check' + (allSelected ? ' on' : '') + (someSelected && !allSelected ? ' partial' : '')} onClick={onSelectAll} title={t('inv.bulk.selectAll')} aria-label={t('inv.bulk.selectAll')} data-help={t('helpTopics.bulk.body')} data-help-title={t('helpTopics.bulk.title')}>{allSelected && <Icon name="check" size={13} stroke={3} />}</button>
    <div className="fak-bulkbar-text"><b>{selectedRows.length ? t('inv.bulk.selectedCount', { n: selectedRows.length }) : t('inv.bulk.selectDocuments')}</b><span>{t('inv.bulk.shownCount', { n: rows.length })}</span></div>
    <div className="row gap-8" style={{ marginLeft: 'auto', flexWrap: 'wrap' }}>
      {selectedRows.length > 0 && <Button size="sm" variant="ghost" onClick={onClear}>{t('inv.bulk.reset')}</Button>}
      {actions.map((action) => <Button key={action.key} size="sm" icon={action.icon} variant={action.variant}
        disabled={!invoiceListBulkTargets(action.key, selectedRows).length} onClick={() => onAction(action.key)}>{action.label}</Button>)}
    </div>
  </div>;
}

// Fristen som foreslaas henger sammen med typen: en betalingspaaminnelse er en
// rask beskjed om noe som allerede har forfalt, mens en purring gir kunden en ny
// reell frist. Foreslaatt verdi byttes bare saa lenge brukeren ikke har skrevet
// inn sin egen.
const INVOICE_REMINDER_DUE_DAYS = { reminder: 1, purring: 7 };

function invoiceListBulkTitle(action) {
  return t('inv.bulk.title.' + action);
}

function invoiceListBulkConfirmLabel(action, sendMode) {
  if (action === 'credit') return t(sendMode ? 'inv.bulk.confirm.creditSend' : 'inv.bulk.confirm.credit');
  if (action === 'issue') return t(sendMode ? 'inv.bulk.confirm.issueSend' : 'inv.bulk.confirm.issue');
  return t('inv.bulk.confirm.' + action);
}

function InvoiceBulkDialog({ action: requestedAction, rows, busy, onClose, onConfirm, initial = null }) {
  const action = requestedAction === 'issueOnly' ? 'issue' : requestedAction;
  const initialDueDays=initial?.dueDate?Math.max(0,Math.round((Date.parse(initial.dueDate)-Date.parse(invoiceListTodayIso()))/86400000)):INVOICE_REMINDER_DUE_DAYS.reminder;
  const [reminderVariant, setReminderVariant] = React.useState(initial?.variant||'reminder');
  const [dueDays, setDueDays] = React.useState(initialDueDays);
  const [dueDaysEdited, setDueDaysEdited] = React.useState(Boolean(initial?.dueDate));
  const [fee, setFee] = React.useState(Number(initial?.fee)||0);
  const [deliveryMode, setDeliveryMode] = React.useState(initial?.deliveryMode||'attachment');
  const [comment, setComment] = React.useState(initial?.comment||'');
  const [commentId, setCommentId] = React.useState('');
  // Kreditering fra lista lager utkast som for. Skal kreditnotaene bokfoeres og
  // sendes med én gang, er det et eget valg — det er den irreversible varianten.
  const [mode, setMode] = React.useState(requestedAction === 'issueOnly' ? 'create' : action === 'credit' ? 'draft' : 'send');
  const [creditReason, setCreditReason] = React.useState(initial?.creditReason||'');
  const targets = invoiceListBulkTargets(action, rows);
  const latestAllowedCreditDate = targets.reduce((latest, invoice) => { const date = invoiceListCreditMinimumDate(invoice); return date > latest ? date : latest; }, '');
  const [creditDate, setCreditDate] = React.useState(() => initial?.date || [invoiceListTodayIso(), latestAllowedCreditDate].sort().at(-1)); const [confirmPaid, setConfirmPaid] = React.useState(false);
  const skipped = Math.max(0, rows.length - targets.length);
  const sendMode = mode === 'send';
  const hasModeChoice = action === 'credit' || action === 'issue';
  // Dokumentet blir opprettet og bokfoert selv om kunden mangler e-post. Det skal staa
  // for brukeren trykker, ikke komme som en overraskelse i oppsummeringen etterpaa.
  const missingEmail = hasModeChoice && sendMode
    ? targets.filter((invoice) => !invoiceListCustomerEmail(invoice)).length
    : 0;
  // Aarsaken er dokumentasjonen bokfoeringsforskriften § 5-2-7 krever naar et salg
  // reverseres, og den staar paa kreditnotaen. Den kreves derfor naar kreditnotaen
  // faktisk blir opprettet — ikke naar den bare legges i utkast.
  const reasonTargets = action === 'issue' ? targets.filter(invoiceListDraftNeedsCreditReason) : [];
  const needsReason = action === 'credit' ? sendMode : reasonTargets.length > 0;
  const showReason = action === 'credit' || reasonTargets.length > 0;
  const safeDueDays = Math.max(0, Math.min(90, Math.round(Number(dueDays) || 0)));
  // Gebyret hoerer til purringen. En betalingspaaminnelse er den vennlige
  // beskjeden for den — den skal ikke gjore kravet stoerre, og et beloep som ble
  // skrevet inn for typen ble byttet skal derfor heller ikke bli med.
  const feeAllowed = reminderVariant === 'purring';
  const safeFee = feeAllowed ? Math.max(0, Math.round(Number(fee) || 0)) : 0;
  const bodyKey = hasModeChoice ? `inv.bulk.body.${action}${sendMode ? 'Send' : ''}` : 'inv.bulk.body.' + action;
  const changeReminderVariant = (next) => {
    setReminderVariant(next);
    if (!dueDaysEdited) setDueDays(INVOICE_REMINDER_DUE_DAYS[next] ?? INVOICE_REMINDER_DUE_DAYS.reminder);
  };
  const invalidCreditDate = action === 'credit' && (!creditDate || creditDate < latestAllowedCreditDate);
  const paidTargets = action === 'credit' ? targets.filter((invoice) => Number(invoice.paidAmount) > 0.005) : [];
  const confirmDisabled = busy || targets.length === 0 || (needsReason && !creditReason.trim()) || invalidCreditDate;
  const confirmOptions = () => ({ variant: reminderVariant, dueDays: safeDueDays, fee: safeFee, mode, send: sendMode, creditReason: creditReason.trim(), date: creditDate, deliveryMode, comment: comment.trim() });
  const confirm = () => paidTargets.length ? setConfirmPaid(true) : onConfirm(action, confirmOptions());
  const rowAmount = (invoice) => action === 'reminder'
    ? invoiceListOutstandingAmount(invoice) + safeFee
    : invoice.amount;
  const rowSub = (invoice) => {
    const parts = [];
    if (action === 'deliver' || action === 'issue' || action === 'delete') parts.push(invoiceListTypeLabel(invoice.type));
    parts.push(invoice.customer);
    if (action === 'send' || action === 'reminder') parts.push(invoiceListCustomerEmail(invoice));
    if (hasModeChoice && sendMode) parts.push(invoiceListCustomerEmail(invoice) || t('inv.bulk.noEmail'));
    if (action === 'reminder') parts.push(t('inv.bulk.newDueDate', { date: invoiceListIsoToNb(invoiceListAddDays(invoiceListTodayIso(), safeDueDays)) }));
    return parts.filter(Boolean).join(' · ');
  };
  if (confirmPaid) return <Modal open portal onClose={() => setConfirmPaid(false)} title={t('inv.credit.paidConfirmTitle')} max={500} footer={<React.Fragment><Button variant="ghost" onClick={() => setConfirmPaid(false)}>{t('common.cancel')}</Button><Button variant="danger-solid" icon="files" disabled={busy} onClick={() => onConfirm(action, confirmOptions())}>{t('inv.credit.paidConfirmContinue')}</Button></React.Fragment>}><p className="t-body" style={{ margin: 0 }}>{t('inv.bulk.creditPaidConfirmBody', { n: paidTargets.length })}</p></Modal>;
  return <Modal open onClose={onClose} title={invoiceListBulkTitle(action)} max={action === 'reminder' ? 840 : 560}
    footer={<React.Fragment>
      <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon={action === 'delete' ? 'trash' : action === 'deliver' ? 'check' : action === 'credit' && !sendMode ? 'files' : 'send'}
        variant={action === 'delete' ? 'danger-solid' : action === 'credit' ? 'danger-solid' : 'primary'}
        disabled={confirmDisabled} onClick={confirm}>{busy ? t('inv.bulk.processing') : invoiceListBulkConfirmLabel(action, sendMode)}</Button>
    </React.Fragment>}>
    <div className="col gap-12">
      <p className="t-body" style={{ margin: 0 }}>{t(bodyKey, { n: targets.length })}</p>
      {hasModeChoice && <Field label={t('inv.bulk.mode')} hint={t(`inv.bulk.mode.hint.${action}${sendMode ? 'Send' : ''}`)} help={t('helpTopics.bulkMode.body')} helpTitle={t('helpTopics.bulkMode.title')}>
        <Segmented block value={mode} onChange={setMode} options={action === 'credit'
          ? [{ value: 'draft', label: t('inv.bulk.mode.draft'), icon: 'save' }, { value: 'send', label: t('inv.bulk.mode.send'), icon: 'send' }]
          : [{ value: 'send', label: t('inv.bulk.mode.send'), icon: 'send' }, { value: 'create', label: t('inv.bulk.mode.create'), icon: 'fileText' }]} />
      </Field>}
      {showReason && <Field label={t('sale.credit.reason')} hint={t(needsReason ? 'inv.bulk.creditReasonRequired' : 'inv.bulk.creditReasonOptional')} help={t('helpTopics.credit.body')} helpTitle={t('helpTopics.credit.title')}>
        <Input placeholder={t('sale.credit.reasonPh')} value={creditReason} onChange={(event) => setCreditReason(event.target.value)} />
      </Field>}
      {action === 'credit' && <Field label={t('inv.credit.date')} hint={invalidCreditDate ? t('inv.bulk.creditDateBeforeInvoices') : t('inv.bulk.creditDateHint')}><Input type="date" min={latestAllowedCreditDate} value={creditDate} onChange={(event) => setCreditDate(event.target.value)} /></Field>}
      {action === 'reminder' && <React.Fragment>
        <div className="fak-reminder-options">
          <Field label={t('inv.bulk.reminderType')} help={t('helpTopics.reminder.body')} helpTitle={t('helpTopics.reminder.title')}><Select value={reminderVariant} onChange={(event) => changeReminderVariant(event.target.value)}><option value="reminder">{t('inv.bulk.reminderNotice')}</option><option value="purring">{t('inv.bulk.reminderDunning')}</option></Select></Field>
          <Field label={t('inv.bulk.reminderDueDays')} help={t('helpTopics.reminder.body')} helpTitle={t('helpTopics.reminder.title')}><Input type="number" min="0" max="90" step="1" value={dueDays} onChange={(event) => { setDueDaysEdited(true); setDueDays(event.target.value); }} /></Field>
          <Field label={t('inv.bulk.reminderFee')} hint={feeAllowed ? undefined : t('inv.bulk.reminderFeeLocked')} help={t('helpTopics.reminder.body')} helpTitle={t('helpTopics.reminder.title')}>
            <Currency value={feeAllowed ? fee : 0} disabled={!feeAllowed} onChange={setFee} />
          </Field>
          <Field label={t('inv.bulk.delivery')} hint={t(deliveryMode === 'link' ? 'sale.send.linkHint' : 'sale.send.pdfHint')} help={t('helpTopics.bulkMode.body')} helpTitle={t('helpTopics.bulkMode.title')}>
            <Select value={deliveryMode} onChange={(event) => setDeliveryMode(event.target.value)}>
              <option value="attachment">{t('inv.bulk.deliveryPdf')}</option>
              <option value="link">{t('inv.bulk.deliveryLink')}</option>
            </Select>
          </Field>
        </div>
        <InvoiceCommentField value={comment} onChange={setComment} savedId={commentId} onSavedIdChange={setCommentId}
          label={t('inv.bulk.comment')} hint={t('inv.bulk.commentHint')} placeholder={t('inv.bulk.commentPh')} />
      </React.Fragment>}
      {skipped > 0 && <div className="alert alert-warning"><span className="alert-ico"><Icon name="alertTri" size={18} /></span><span>{t('inv.bulk.skip.' + action, { n: skipped })}</span></div>}
      {missingEmail > 0 && <div className="alert alert-info"><span className="alert-ico"><Icon name="info" size={18} /></span><span>{t('inv.bulk.skip.noEmail', { n: missingEmail })}</span></div>}
      <div className="cn-lines">
        <div className="cn-lines-hd"><span style={{ flex: 1 }}>{t('inv.bulk.document')}</span><span style={{ width: 130, textAlign: 'right' }}>{t('inv.bulk.amount')}</span></div>
        {targets.slice(0, 8).map((invoice) => <div key={invoice.id} className="cn-line">
          <span style={{ flex: 1, minWidth: 0 }}><b>{invoice.num || t('inv.draftPlaceholder')}</b><span className="t-xs muted" style={{ display: 'block', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rowSub(invoice)}</span></span>
          <b className="t-num" style={{ width: 130, textAlign: 'right' }}>{fmt(rowAmount(invoice))}</b>
        </div>)}
        {targets.length > 8 && <div className="cn-line"><span className="t-sm muted" style={{ fontWeight: 800 }}>{t('inv.bulk.more', { n: targets.length - 8 })}</span></div>}
      </div>
    </div>
  </Modal>;
}

// Et dokument som ble opprettet men ikke sendt er ikke en feil — det mangler bare en
// e-postadresse. De to tellene holdes derfor fra hverandre i oppsummeringen.
function invoiceListBulkSummary(messageKey, done, failed, notSent = 0) {
  const suffix = (notSent ? t('inv.bulk.toast.notSentSuffix', { n: notSent }) : '')
    + (failed ? t('inv.bulk.toast.failedSuffix', { n: failed }) : '');
  return {
    message: t(messageKey, { n: done }) + suffix + '.',
    tone: failed || notSent ? 'warning' : 'success',
  };
}

// Sending av et opprettet dokument: samme kall som sendedialogen paa dokumentet,
// med e-postadressen som ligger paa kunden.
async function invoiceListBulkSendDocument(payloadSource, uiType, documentNumber, email) {
  await window.PlummeBackend.sendInvoiceDocument(invoiceListSendPayload(payloadSource, email), {
    deliveryMode: 'attachment',
    emailSubject: invoiceListSendSubject({ type: uiType, num: documentNumber || '' }),
  });
}

// Payload-kilden for et dokument backend nettopp opprettet. `invoiceListSendPayload`
// leser `_backend` for selve dokumentet og `customer` for navnet.
function invoiceListBulkCreatedSource(backendInvoice) {
  return { _backend: backendInvoice, customer: backendInvoice.customer?.name || '' };
}

// Kjorer handlingen dokument for dokument og teller opp hva som gikk gjennom. Ett
// dokument som feiler skal ikke stoppe resten — brukeren trenger aa vite hvor mange
// som gikk ut, ikke bare at noe feilet.
async function invoiceListRunBulkAction(action, rows, options = {}) {
  const backend = window.PlummeBackend || {};
  const targets = invoiceListBulkTargets(action, rows);
  const creditReason = String(options.creditReason || '').trim();
  let done = 0;
  let failed = 0;

  if (action === 'deliver') {
    if (!backend.markInvoiceDeliveredExternally) throw new Error(t('inv.bulk.err.deliverNotConnected'));
    for (const invoice of targets) {
      try {
        await backend.markInvoiceDeliveredExternally(invoice);
        done += 1;
      } catch (error) { failed += 1; }
    }
    return invoiceListBulkSummary('inv.bulk.toast.delivered', done, failed);
  }

  if (action === 'send') {
    if (!backend.sendInvoiceDocument) throw new Error(t('inv.bulk.err.sendNotConnected'));
    for (const invoice of targets) {
      try {
        await invoiceListBulkSendDocument(invoice, invoice.type, invoice.num, invoiceListCustomerEmail(invoice));
        done += 1;
      } catch (error) { failed += 1; }
    }
    return invoiceListBulkSummary('inv.bulk.toast.sent', done, failed);
  }

  if (action === 'reminder') {
    if (!backend.sendInvoiceDocument) throw new Error(t('inv.bulk.err.sendNotConnected'));
    const dueDate = invoiceListAddDays(invoiceListTodayIso(), options.dueDays);
    const variant = options.variant === 'purring' ? 'purring' : 'reminder';
    // Gebyret finnes bare paa purringen. Dialogen nuller det allerede ut naar
    // typen er betalingspaaminnelse, og kjoeringen holder paa den samme regelen.
    const reminderFee = variant === 'purring' ? Math.max(0, Number(options.fee) || 0) : 0;
    const reminderDelivery = options.deliveryMode === 'link' ? 'link' : 'attachment';
    const reminderComment = String(options.comment || '').trim();
    for (const invoice of targets) {
      try {
        const outstandingAmount = invoiceListOutstandingAmount(invoice);
        await backend.sendInvoiceDocument(invoiceListSendPayload(invoice, invoiceListCustomerEmail(invoice)), {
          deliveryMode: reminderDelivery,
          variant,
          emailMessage: reminderComment,
          // Emnefeltet gaar til kunden og foelger dokumentets spraak, ikke brukerens
          // UI-spraak — samme grunn som `invoiceListSendSubject` er hardkodet.
          emailSubject: `${variant === 'purring' ? 'Purring' : 'Betalingspåminnelse'}: faktura ${invoice.num || ''} fra Plumme`.trim(),
          followUpPaymentSummary: {
            paidAmount: Math.max(0, Number(invoice.paidAmount) || 0),
            remainingAmount: outstandingAmount + reminderFee,
            originalRemainingAmount: outstandingAmount,
            reminderFee,
            dueDate,
          },
        });
        done += 1;
      } catch (error) { failed += 1; }
    }
    return invoiceListBulkSummary(variant === 'purring' ? 'inv.bulk.toast.dunned' : 'inv.bulk.toast.reminded', done, failed);
  }

  if (action === 'credit') {
    if (!backend.creditInvoice) throw new Error(t('inv.bulk.err.creditNotConnected'));
    const issue = Boolean(options.send);
    if (issue && !backend.sendInvoiceDocument) throw new Error(t('inv.bulk.err.sendNotConnected'));
    // Validate the complete selection before the first write. Without this
    // preflight, a later invoice in a delivered VAT term could leave a partial
    // batch (for example one draft created and one rejected).
    for (const invoice of targets) {
      await backend.creditInvoice(invoice, { creditReason, issue, date: options.date, v: 1 });
    }
    let sent = 0;
    let firstError = null;
    for (const invoice of targets) {
      try {
        // Kreditnotaen opprettes av backend. Skal den sendes, blir den bokfoert i
        // samme kall — den skal ikke ligge som utkast med et nummer den ikke har.
        const credit = await backend.creditInvoice(invoice, { creditReason, issue, date: options.date });
        done += 1;
        const email = invoiceListCustomerEmail(invoice);
        if (issue && credit && email) {
          await invoiceListBulkSendDocument(invoiceListBulkCreatedSource(credit), 'kreditnota', credit.invoiceNumber, email);
          sent += 1;
        }
      } catch (error) { failed += 1; firstError ||= error; }
    }
    if (failed && !done && firstError) throw firstError;
    const withFailureDetail = (summary) => failed && firstError?.message ? { ...summary, message: `${summary.message} ${firstError.message}` } : summary;
    if (!issue) return withFailureDetail(invoiceListBulkSummary('inv.bulk.toast.credited', done, failed));
    return withFailureDetail(invoiceListBulkSummary('inv.bulk.toast.creditedSent', sent, failed, done - sent));
  }

  if (action === 'issue') {
    if (!backend.issueInvoiceDraft) throw new Error(t('inv.bulk.err.issueNotConnected'));
    const send = Boolean(options.send);
    if (send && !backend.sendInvoiceDocument) throw new Error(t('inv.bulk.err.sendNotConnected'));
    let sent = 0;
    for (const invoice of targets) {
      try {
        const issued = await backend.issueInvoiceDraft(invoice, { creditReason });
        done += 1;
        const email = invoiceListCustomerEmail(invoice);
        if (send && issued && email) {
          await invoiceListBulkSendDocument(invoiceListBulkCreatedSource(issued), invoice.type, issued.invoiceNumber, email);
          sent += 1;
        }
      } catch (error) { failed += 1; }
    }
    if (!send) return invoiceListBulkSummary('inv.bulk.toast.issued', done, failed);
    return invoiceListBulkSummary('inv.bulk.toast.issuedSent', sent, failed, done - sent);
  }

  if (action === 'delete') {
    if (!backend.deleteInvoice) throw new Error(t('inv.err.deleteNotConnected'));
    for (const invoice of targets) {
      try {
        const deleted = await backend.deleteInvoice(invoice);
        if (deleted === false) failed += 1; else done += 1;
      } catch (error) { failed += 1; }
    }
    return invoiceListBulkSummary('inv.bulk.toast.deleted', done, failed);
  }

  throw new Error(t('inv.bulk.err.failed'));
}

Object.assign(window, { InvoiceBulkBar, InvoiceBulkDialog });
